import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Calendar, Plus, Trash2, Edit2, Save, X, RefreshCw, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAM_TYPES = ['SAT', 'ACT', 'PSAT', 'AP_Exams', 'ASVAB', 'GED', 'TEAS', 'CLEP', 'TOEFL'];

const empty = () => ({
  exam_type: 'SAT',
  date: '',
  registration_deadline: '',
  late_deadline: '',
  note: '',
});

export default function AdminExamDatesPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty());
  const [filterType, setFilterType] = useState('all');

  const todayIso = new Date().toISOString().slice(0, 10);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/exam-dates`, {
        ...getAuthHeader(),
        withCredentials: true,
      });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load exam dates');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'super_admin', 'org_admin'].includes(user.role)) {
      toast.error('Admin access required');
      navigate('/app/dashboard');
      return;
    }
    fetchItems();
  }, [user, navigate, fetchItems]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({
      exam_type: item.exam_type || 'SAT',
      date: item.date || '',
      registration_deadline: item.registration_deadline || '',
      late_deadline: item.late_deadline || '',
      note: item.note || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCreating(false);
    setDraft(empty());
  };

  const saveEdit = async () => {
    if (!draft.exam_type || !draft.date) {
      toast.error('Exam type and date are required');
      return;
    }
    try {
      if (creating) {
        await axios.post(`${API}/admin/exam-dates`, draft, {
          ...getAuthHeader(),
          withCredentials: true,
        });
        toast.success('Exam date created');
      } else {
        await axios.put(`${API}/admin/exam-dates/${editingId}`, draft, {
          ...getAuthHeader(),
          withCredentials: true,
        });
        toast.success('Exam date updated');
      }
      cancelEdit();
      fetchItems();
    } catch (err) {
      showFriendlyError(err, { fallback: 'Save failed' });
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this exam date?')) return;
    try {
      await axios.delete(`${API}/admin/exam-dates/${id}`, {
        ...getAuthHeader(),
        withCredentials: true,
      });
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const reseed = async () => {
    if (!window.confirm('Re-seed exam_dates from the in-code constants? This will WIPE existing entries.')) return;
    try {
      await axios.post(`${API}/admin/exam-dates/seed?force=true`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
      });
      toast.success('Re-seeded from defaults');
      fetchItems();
    } catch (err) {
      toast.error('Re-seed failed');
    }
  };

  const filtered = items.filter((it) => filterType === 'all' || it.exam_type === filterType);
  const isPast = (d) => /^\d{4}-\d{2}-\d{2}/.test(d) && d.slice(0, 10) < todayIso;

  return (
    <div className="min-h-screen bg-[#0a1729] text-white" data-testid="admin-exam-dates-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Exam Dates</h1>
              <p className="text-sm text-slate-300">Manage SAT, ACT, AP and other test dates shown across the app</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => { setCreating(true); setEditingId(null); setDraft(empty()); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-exam-date-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Date
            </Button>
            <Button
              onClick={reseed}
              variant="outline"
              className="border-slate-700"
              data-testid="reseed-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Re-seed
            </Button>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="exam-type-filter">
          {['all', ...EXAM_TYPES].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                filterType === t ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
              data-testid={`filter-${t}`}
            >
              {t === 'all' ? 'All' : t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        {/* Create row */}
        {creating && (
          <div className="bg-slate-800/50 border border-blue-500/40 rounded-lg p-4 mb-4" data-testid="create-row">
            <EditForm draft={draft} setDraft={setDraft} onSave={saveEdit} onCancel={cancelEdit} />
          </div>
        )}

        {/* Table */}
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase text-slate-300 border-b border-slate-700">
            <div className="col-span-2">Exam</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Reg. Deadline</div>
            <div className="col-span-2">Late Deadline</div>
            <div className="col-span-3">Note</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-300">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-300" data-testid="empty-state">
              <AlertCircle className="w-6 h-6 mx-auto mb-2" />
              No exam dates yet. Click "Add Date" or "Re-seed" to populate from defaults.
            </div>
          ) : (
            filtered.map((it) => {
              const isEditing = editingId === it.id;
              const past = isPast(it.date);
              return (
                <div
                  key={it.id}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-700/50 items-center ${past ? 'opacity-50' : ''}`}
                  data-testid={`exam-row-${it.id}`}
                >
                  {isEditing ? (
                    <div className="col-span-12">
                      <EditForm draft={draft} setDraft={setDraft} onSave={saveEdit} onCancel={cancelEdit} />
                    </div>
                  ) : (
                    <>
                      <div className="col-span-2 font-medium text-white">{it.exam_type}</div>
                      <div className="col-span-2 text-slate-300 text-sm">
                        {it.date}
                        {past && <span className="ml-2 text-xs text-amber-400">(past)</span>}
                      </div>
                      <div className="col-span-2 text-slate-300 text-sm">{it.registration_deadline || '-'}</div>
                      <div className="col-span-2 text-slate-300 text-sm">{it.late_deadline || '-'}</div>
                      <div className="col-span-3 text-slate-300 text-xs truncate">{it.note || '-'}</div>
                      <div className="col-span-1 flex justify-end gap-1">
                        <button
                          onClick={() => startEdit(it)}
                          className="p-1.5 rounded hover:bg-slate-700 text-blue-400"
                          data-testid={`edit-${it.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteItem(it.id)}
                          className="p-1.5 rounded hover:bg-slate-700 text-red-400"
                          data-testid={`delete-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        <p className="text-xs text-slate-300 mt-4">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          Test dates older than today are auto-hidden from public pages but kept here for history. Use ISO format (YYYY-MM-DD) for dates so auto-filter works.
        </p>
      </div>
    </div>
  );
}

function EditForm({ draft, setDraft, onSave, onCancel }) {
  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <select
        value={draft.exam_type}
        onChange={(e) => update('exam_type', e.target.value)}
        className="col-span-2 bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-sm text-white"
        data-testid="form-exam-type"
      >
        {EXAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <Input
        value={draft.date}
        onChange={(e) => update('date', e.target.value)}
        placeholder="YYYY-MM-DD"
        className="col-span-2 bg-slate-900 border-slate-700 text-sm"
        data-testid="form-date"
      />
      <Input
        value={draft.registration_deadline}
        onChange={(e) => update('registration_deadline', e.target.value)}
        placeholder="YYYY-MM-DD"
        className="col-span-2 bg-slate-900 border-slate-700 text-sm"
        data-testid="form-reg-deadline"
      />
      <Input
        value={draft.late_deadline}
        onChange={(e) => update('late_deadline', e.target.value)}
        placeholder="YYYY-MM-DD (optional)"
        className="col-span-2 bg-slate-900 border-slate-700 text-sm"
        data-testid="form-late-deadline"
      />
      <Input
        value={draft.note}
        onChange={(e) => update('note', e.target.value)}
        placeholder="Optional note"
        className="col-span-3 bg-slate-900 border-slate-700 text-sm"
        data-testid="form-note"
      />
      <div className="col-span-1 flex justify-end gap-1">
        <button onClick={onSave} className="p-1.5 rounded bg-emerald-600 hover:bg-emerald-700" data-testid="form-save">
          <Save className="w-4 h-4" />
        </button>
        <button onClick={onCancel} className="p-1.5 rounded bg-slate-700 hover:bg-slate-600" data-testid="form-cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
